import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation, useNavigate } from 'react-router';
import classNames from 'classnames';

import { useDispatch, useSelector } from 'hooks';
import {
  hasCodeLogin as hasCodeLoginSelector,
  hasPhoneNumberLogin as hasPhoneNumberLoginSelector,
  hasLicensePlateLogin as hasLicensePlateLoginSelector,
  hasQrCodeReader as hasQrCodeReaderSelector,
  showVideoGuide
} from 'modules/dealers/selectors';

import QrCodePanel from './QrCodePanel';
import QrCodeInfoModal from './modals/QrCodeInfoModal';
import EmailCodeModal from './modals/EmailCodeModal';
import LicensePlateModal from './modals/LicensePlateModal';
import LoginMethodButton from './LoginMethodButton';
import { PageHeader } from 'components/ui';
import Footer from 'components/ui/PageFooter';
import PhoneNumberModal from './modals/PhoneNumberModal';
import { resetLoginData } from 'modules/auth/actions';
import { SelfOfficeLoginMethod } from 'modules/dealers/types/ContextResponse';


enum ModalsEnum {
  QR_INFO = 'QR_INFO',
  QR_CODE = 'QR_CODE',
  PHONE_NUMBER='PHONE_NUMBER',
  LICENSE_PLATE = 'LICENSE_PLATE',
}

export type LoginCardLayoutProps = {
  onLoginSuccess: (loginMethod: SelfOfficeLoginMethod) => void;
  onLoginError: (error: unknown) => void;
};

const subtitle = <FormattedMessage id="login.kiosk.continue" defaultMessage="Continue" />;
const fastestOptionLabel =<FormattedMessage id="login.kiosk.fastestOption" defaultMessage="Fastest option"/>;
const noVideoStyle = '!h-[300px]';

const LoginCardLayout: React.FC<LoginCardLayoutProps> = ({ onLoginError, onLoginSuccess }) => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const dispatch = useDispatch();

  const showQrVideoGuide = useSelector(showVideoGuide);
  const hasQrCodeReader = useSelector(hasQrCodeReaderSelector);
  const hasLicensePlateLogin = useSelector(hasLicensePlateLoginSelector);
  const hasPhoneNumberLogin = useSelector(hasPhoneNumberLoginSelector);
  const hasCodeLogin = useSelector(hasCodeLoginSelector);

  const [activeModal, setActiveModal] = React.useState<ModalsEnum|undefined>(undefined);

  const handleOnPrev = React.useCallback(() => navigate(`/${search}`), [navigate, search]);

  const emailCodeButton = React.useMemo(() =>  (
    hasCodeLogin &&
    <LoginMethodButton
      subtitle={subtitle}
      key="login-email-code"
      testId="login-method-email-code"
      onClick={() => setActiveModal(ModalsEnum.QR_CODE)}
      className={!showQrVideoGuide ? noVideoStyle:''}
      iconName={hasQrCodeReader ? 'fb-icon--mail' : undefined}
      badgeLabel={!hasQrCodeReader ? fastestOptionLabel : undefined}
      title={<FormattedMessage id="login.kiosk.emailCode.title" defaultMessage="I have my code received by email" />}
    />
  ), [showQrVideoGuide, hasQrCodeReader, hasCodeLogin]);

  const phoneNumberCodeButton = React.useMemo(() =>  (
    hasPhoneNumberLogin &&
    <LoginMethodButton
      subtitle={subtitle}
      key="login-phone-number"
      testId="login-method-phone-number"
      className={!showQrVideoGuide ? noVideoStyle:''}
      onClick={() => setActiveModal(ModalsEnum.PHONE_NUMBER)}
      iconName="fb-icon--phone"
      title={<FormattedMessage id="login.kiosk.phoneNumber.title" defaultMessage="I have my phone number" />}
    />
  ), [showQrVideoGuide, hasPhoneNumberLogin]);

  const licensePlateButton = React.useMemo(() => {
    const isAlone = !hasCodeLogin && !hasPhoneNumberLogin;
    return hasLicensePlateLogin && (
      <LoginMethodButton
        subtitle={subtitle}
        key="login-license-plate"
        testId="login-method-license-plate"
        iconName="fb-icon--ext-configurator"
        onClick={() => setActiveModal(ModalsEnum.LICENSE_PLATE)}
        className={classNames({ [noVideoStyle]: !showQrVideoGuide, 'min-h-[400px]': isAlone })}
        title={<FormattedMessage id="login.kiosk.licensePlate.title" defaultMessage="I have my license plate" />}
      />
    );
  }, [showQrVideoGuide, hasCodeLogin, hasPhoneNumberLogin, hasLicensePlateLogin]);

  const children = [
    emailCodeButton,
    phoneNumberCodeButton,
    licensePlateButton,
  ].filter(Boolean);

  const handleOnClose = () => {
    dispatch(resetLoginData());
    setActiveModal(undefined);
  };

  return (
    <>
      <PageHeader
        hideSteps
        onPrev={handleOnPrev}
        title={<FormattedMessage id="login.title" defaultMessage="Login" />}
      />
      <div className="main-content lg:!max-w-8xl">
        <main className="content !my-auto sm:!mt-6 mx-auto sm:!mx-0">
          <div data-testid="login-card-layout-title" className="text-center text-btn-secondary-text font-bold !mb-16 !pt-0 text-s sm:!mb-10">
            <FormattedMessage
              id="login.kiosk.title"
              defaultMessage="How would you like to find your appointment? Choose one method."
            />
          </div>

          {showQrVideoGuide && (
            <div className="flex gap-9 w-full items-stretch">
              <div className="flex-1 flex flex-col">
                <QrCodePanel
                  badgeLabel={fastestOptionLabel}
                  onClick={() => setActiveModal(ModalsEnum.QR_INFO)}
                />
              </div>
              <div className="flex-1 flex flex-col gap-8">
                {children}
              </div>
            </div>
          )}

          {!showQrVideoGuide && (
            <div className="flex gap-8 w-full items-stretch sm:flex-col">
              {children}
            </div>
          )}

          {activeModal === ModalsEnum.QR_INFO && <QrCodeInfoModal onClose={handleOnClose} />}
          {activeModal === ModalsEnum.QR_CODE && <EmailCodeModal onError={onLoginError} onClose={handleOnClose} onSuccess={onLoginSuccess} />}
          {activeModal === ModalsEnum.PHONE_NUMBER && <PhoneNumberModal onError={onLoginError} onClose={handleOnClose} onSuccess={onLoginSuccess} />}
          {activeModal === ModalsEnum.LICENSE_PLATE && <LicensePlateModal onError={onLoginError} onClose={handleOnClose} onSuccess={onLoginSuccess} />}
        </main>
        <Footer
          onPrev={handleOnPrev}
          shouldDisplayBackButton
          hideNextButton
        />
      </div>
    </>
  );
};

export default LoginCardLayout;
